/**
 * Extract GFM-style pipe tables from a markdown answer.
 *
 * The knowledge agent occasionally emits tables in its responses (model
 * comparisons, BCX line-item breakdowns, etc.). The chat export menu offers
 * a "Tables → Excel / CSV" item only when the answer actually contains at
 * least one table; this helper is what decides whether the option renders,
 * and supplies the rows when the user picks Excel or CSV.
 *
 * Detection is conservative: a header row of `| col | col |`, then a
 * separator row of dashes (and only dashes, colons and pipes), then one or
 * more body rows starting with `|`. The table's display name is the
 * nearest preceding `##`/`###` heading text, or the last non-blank
 * paragraph line if no recent heading.
 */

export interface ExtractedTable {
  /** Stable id so React keys + Excel sheet names stay deterministic. */
  id: string;
  /** Human label — preceding heading, fallback to "Table N". */
  heading: string;
  headers: string[];
  rows: string[][];
}

export function extractMarkdownTables(answer: string): ExtractedTable[] {
  const lines = answer.replace(/\r\n/g, '\n').split('\n');
  const tables: ExtractedTable[] = [];
  let lastHeading: string | null = null;
  let lastNonBlankLine: string | null = null;
  let i = 0;

  while (i < lines.length) {
    const line = lines[i] ?? '';
    const trimmed = line.trim();

    // Track context for naming the next table.
    const headingMatch = trimmed.match(/^#{1,4}\s+(.*)$/);
    if (headingMatch) {
      lastHeading = headingMatch[1].trim();
      i += 1;
      continue;
    }
    if (trimmed === '') {
      i += 1;
      continue;
    }

    // Table detection — header row + separator row.
    const separator = lines[i + 1];
    if (isTableHeaderRow(line) && separator !== undefined && isTableSeparator(separator)) {
      const headers = splitRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && /\|/.test(lines[i] ?? '')) {
        const cells = splitRow(lines[i] ?? '');
        // Pad / trim to header length so callers don't have to guard.
        if (cells.length < headers.length) {
          while (cells.length < headers.length) cells.push('');
        } else if (cells.length > headers.length) {
          cells.length = headers.length;
        }
        rows.push(cells);
        i += 1;
      }
      const ordinal = tables.length + 1;
      const heading = lastHeading ?? truncateForLabel(lastNonBlankLine ?? '') ?? `Table ${ordinal}`;
      tables.push({
        id: `tbl-${ordinal}`,
        heading,
        headers,
        rows,
      });
      // Consume the heading so two tables in the same section don't
      // collapse to the same name — they roll over to "Table N" instead.
      lastHeading = null;
      lastNonBlankLine = null;
      continue;
    }

    lastNonBlankLine = trimmed;
    i += 1;
  }

  return tables;
}

function isTableHeaderRow(line: string): boolean {
  if (!line.includes('|')) return false;
  // Must contain at least one cell between pipes.
  return /\|\s*\S[^|\n]*\s*\|/.test(line) || /^\s*\|.+\|/.test(line);
}

function isTableSeparator(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed.includes('|')) return false;
  return /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?$/.test(trimmed);
}

function splitRow(row: string): string[] {
  const trimmed = row.trim().replace(/^\|/, '').replace(/\|$/, '');
  return trimmed.split('|').map((c) => stripInlineMarkdown(c.trim()));
}

/**
 * Cell text is rendered into Excel / CSV as plain values. Keep inline
 * markdown out of the spreadsheet — strip `**bold**`, `*italic*`,
 * `` `code` ``, and unwrap `[text](url)` to its label.
 */
function stripInlineMarkdown(text: string): string {
  return text
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/(^|[^*])\*([^*]+)\*/g, '$1$2')
    .replace(/`([^`]+)`/g, '$1');
}

function truncateForLabel(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (trimmed.length <= 60) return trimmed;
  return `${trimmed.slice(0, 57)}…`;
}

/**
 * Serialize a single table to RFC-4180 CSV. Cells containing `,`, `"`, or
 * newlines get wrapped in double-quotes; embedded `"` is doubled.
 */
export function tableToCsv(table: ExtractedTable): string {
  const rows = [table.headers, ...table.rows];
  return rows.map((r) => r.map(csvCell).join(',')).join('\r\n');
}

function csvCell(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Sheet names in xlsx are capped at 31 chars and forbid the chars
 * `\ / ? * [ ] :`. Truncate + sanitise; fall back to "Table N" if the
 * result is empty.
 */
export function sheetNameFor(table: ExtractedTable, fallbackIndex: number): string {
  const cleaned = table.heading
    .replace(/[\\/?*[\]:]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 31);
  return cleaned.length > 0 ? cleaned : `Table ${fallbackIndex + 1}`;
}
